import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { FONT_HEADING } from "./constants";
import logo from "../assets/image/eblackdog_logo.png";

const API_BASE_URL = "https://patrol-react.eblackdog.net/api";

const MENU_CONFIG = [
  {
    key: "dashboard",
    title: "Dashboard",
    path: "/dashboard",
    moduleNames: ["Dashboard"],
  },
  {
    key: "users",
    title: "User Details",
    moduleNames: ["User Details", "Users", "User Management"],
    children: [
      {
        title: "User List",
        path: "/user_list",
        subNames: ["User List"],
      },
      {
        title: "Add New User",
        path: "/add_user",
        subNames: ["Add New User"],
      },
      {
        title: "Sentinel User List",
        path: "/sentinel_user_list",
        subNames: ["Sentinel User List"],
      },
      {
        title: "User Incident Types",
        path: "/add_incidents",
        subNames: ["User Incident Types"],
      },
    ],
  },
  {
    key: "routes",
    title: "Route Management",
    moduleNames: ["Route Management", "Routes"],
    children: [
      {
        title: "Create Route",
        path: "/create_route",
        subNames: ["Create Route"],
      },
      {
        title: "Route List",
        path: "/route_list",
        subNames: ["Route List"],
      },
      {
        title: "Sentinel Route List",
        path: "/sentinel_route_list",
        subNames: ["Sentinel Route List"],
      },
    ],
  },
  {
    key: "qr",
    title: "QR Codes",
    moduleNames: ["QR Codes", "QR Code Module", "QR Code", "QR"],
    children: [
      {
        title: "Patrol QR",
        path: "/patrol-qr",
        subNames: ["Patrol QR"],
      },
      {
        title: "Sentinel QR",
        path: "/sentinel-qr",
        subNames: ["Sentinel QR"],
      },
    ],
  },
  {
    key: "installation",
    title: "Installation Module",
    path: "/installation",
    moduleNames: ["Installation Module", "Installation"],
  },
  {
    key: "reports",
    title: "Reporting System",
    moduleNames: ["Reporting System", "Reports"],
    children: [
      {
        title: "Summaries Module",
        path: "/summaries",
        subNames: ["Summaries Module"],
      },
      {
        title: "Analytics Module",
        path: "/analytics",
        subNames: ["Analytics Module"],
      },
      {
        title: "Export Module",
        path: "/export",
        subNames: ["Export Module"],
      },
      {
        title: "Custom Module",
        path: "/custom",
        subNames: ["Custom Module"],
      },
    ],
  },
  {
    key: "sentinel_reports",
    title: "Sentinel Reports",
    path: "/sentinel_reports",
    moduleNames: ["Sentinel Reports"],
  },
  {
    key: "incampus",
    title: "InCampus Module",
    path: "/incampus",
    moduleNames: ["InCampus Module", "InCampus"],
  },
];

export default function Sidebar({
  skin,
  sidebarOpen,
  setSidebarOpen,
  routePage,
  onLogout,
}) {
  const location = useLocation();
  const currentPath = location.pathname;

  const [openMenu, setOpenMenu] = useState("");
  const [permissionRows, setPermissionRows] = useState([]);
  const [permissionLoading, setPermissionLoading] = useState(false);

  const token =
    localStorage.getItem("authToken") ||
    localStorage.getItem("token") ||
    localStorage.getItem("guardToken");

  const guardData = (() => {
    try {
      return JSON.parse(localStorage.getItem("guardData") || "{}");
    } catch {
      return {};
    }
  })();

  const userData = (() => {
    try {
      return JSON.parse(localStorage.getItem("userData") || "{}");
    } catch {
      return {};
    }
  })();

  const memberId =
    localStorage.getItem("memberId") ||
    guardData.memberId ||
    guardData.fld_memberId ||
    userData.memberId ||
    userData.fld_memberId ||
    "";

  const loginType =
    localStorage.getItem("guardLoginType") ||
    guardData.loginType ||
    userData.loginType ||
    "";

  const isGuard =
    localStorage.getItem("isGuardLoggedIn") === "true" ||
    loginType === "patrol" ||
    loginType === "sentinel";

  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/[_-]+/g, " ")
      .replace(/\s+/g, " ");

  const yes = (value) => {
    return (
      value === true ||
      value === 1 ||
      value === "1" ||
      normalize(value) === "true" ||
      normalize(value) === "yes"
    );
  };

  useEffect(() => {
    const fetchGuardPermissions = async () => {
      if (!isGuard || !memberId) {
        setPermissionRows([]);
        return;
      }

      try {
        setPermissionLoading(true);

        const res = await fetch(
          `${API_BASE_URL}/guard-permissions/sidebar/${encodeURIComponent(
            memberId
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
          }
        );

        const result = await res.json().catch(() => ({}));

        if (result.success) {
          setPermissionRows(result.data || result.permissions || []);
        } else {
          setPermissionRows([]);
        }
      } catch (error) {
        console.error("[Sidebar] Permission fetch error:", error);
        setPermissionRows([]);
      } finally {
        setPermissionLoading(false);
      }
    };

    fetchGuardPermissions();
  }, [isGuard, memberId, token]);

  const hasModulePermission = (item) => {
    if (!isGuard) return true;

    return permissionRows.some((row) => {
      const department = normalize(row.fld_department || row.department);
      const moduleName = normalize(row.modules || row.module || row.title);
      const canView = yes(row.fld_view ?? row.view ?? row.approve);

      return (
        canView &&
        item.moduleNames.some((name) => {
          const target = normalize(name);
          return department === target || moduleName === target;
        })
      );
    });
  };

  const hasSubPermission = (child, parent) => {
    if (!isGuard) return true;

    return permissionRows.some((row) => {
      const department = normalize(row.fld_department || row.department);
      const moduleName = normalize(row.modules || row.module || row.title);

      const subModule = normalize(
        row.fld_submodules || row.submodules || row.submodule
      );

      const canView = yes(row.fld_view ?? row.view ?? row.approve);

      const parentMatched = parent.moduleNames.some((name) => {
        const target = normalize(name);
        return department === target || moduleName === target;
      });

      const childMatched = child.subNames.some(
        (name) => normalize(name) === subModule
      );

      return canView && parentMatched && childMatched;
    });
  };

  const visibleMenu = useMemo(() => {
    if (!isGuard) return MENU_CONFIG;

    return MENU_CONFIG.map((item) => {
      if (!hasModulePermission(item)) return null;

      if (!item.children) return item;

      const allowedChildren = item.children.filter((child) =>
        hasSubPermission(child, item)
      );

      if (allowedChildren.length === 0) return null;

      return {
        ...item,
        children: allowedChildren,
      };
    }).filter(Boolean);
  }, [isGuard, permissionRows]);

  const getMenuFromPath = (path) => {
    const normalizedPath = normalize(path);

    for (const item of visibleMenu) {
      if (normalize(item.path) === normalizedPath) return item.key;

      if (
        item.children?.some(
          (child) => normalize(child.path) === normalizedPath
        )
      ) {
        return item.key;
      }
    }

    return "";
  };

  useEffect(() => {
    const menu = getMenuFromPath(currentPath);
    if (menu) setOpenMenu(menu);
  }, [currentPath, visibleMenu]);

  const toggleMenu = (menuName) => {
    setOpenMenu((prev) => (prev === menuName ? "" : menuName));
  };

  const closeSidebar = () => setSidebarOpen(false);

  const isPathActive = (path) => normalize(currentPath) === normalize(path);

  const isMenuActive = (menuName) => getMenuFromPath(currentPath) === menuName;

  const mainLinkClass = (path) =>
    `block px-5 py-3.5 text-sm no-underline transition ${
      isPathActive(path)
        ? "bg-white/15 text-white border-l-4 border-white"
        : "text-white/90 hover:bg-white/10 hover:text-white border-l-4 border-transparent"
    }`;

  const menuButtonClass = (menuName) =>
    `w-full text-left px-5 py-3.5 text-sm transition border-l-4 ${
      isMenuActive(menuName)
        ? "bg-white/15 text-white border-white"
        : "bg-transparent text-white/90 hover:bg-white/10 hover:text-white border-transparent"
    }`;

  const subLinkClass = (path) =>
    `block w-full py-3 pr-5 pl-10 text-sm no-underline transition border-l-4 ${
      isPathActive(path)
        ? "bg-white/15 text-white border-white"
        : "text-white/80 hover:bg-white/10 hover:text-white border-transparent"
    }`;

  return (
    <>
      <button
        className="fixed left-4 top-4 z-[1200] rounded-xl px-3.5 py-2.5 text-white lg:hidden"
        style={{ background: skin.accent }}
        onClick={() => setSidebarOpen(true)}
      >
        ☰
      </button>

      {sidebarOpen && (
        <div
          onClick={closeSidebar}
          className="fixed inset-0 z-[999] bg-black/40 lg:hidden"
        />
      )}

      <aside
        className={`fixed bottom-0 left-0 top-0 z-[1000] w-[270px] overflow-y-auto bg-slate-900 text-white transition-transform duration-300 lg:translate-x-0 ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        }`}
      >
        <div
          className="px-4 py-6 text-center"
          style={{ background: skin.gradient }}
        >
          <div className="flex flex-col items-center">
            <img
              src={logo}
              alt="Eblackdog Logo"
              className="mb-3 w-[90px] rounded-xl bg-white p-2"
            />

            <h1
              className="m-0 text-lg leading-relaxed"
              style={{ fontFamily: FONT_HEADING }}
            >
              Eblackdog Security Patrol & Sentinel Command Centre
            </h1>
          </div>
        </div>

        <nav className="flex min-h-[calc(100vh-150px)] flex-col justify-between">
          <ul className="m-0 list-none py-4 pl-0">
            {permissionLoading && isGuard && (
              <li className="px-5 py-3 text-sm text-white/70">
                Loading menu...
              </li>
            )}

            {!permissionLoading &&
              visibleMenu.map((item) => {
                if (item.children?.length) {
                  return (
                    <li key={item.key}>
                      <button
                        type="button"
                        className={menuButtonClass(item.key)}
                        onClick={() => toggleMenu(item.key)}
                      >
                        {item.title}
                      </button>

                      {openMenu === item.key && (
                        <ul className="m-0 list-none bg-white/5 p-0">
                          {item.children.map((child) => (
                            <li key={child.path}>
                              <Link
                                to={child.path}
                                onClick={closeSidebar}
                                className={subLinkClass(child.path)}
                              >
                                {child.title}
                              </Link>
                            </li>
                          ))}
                        </ul>
                      )}
                    </li>
                  );
                }

                return (
                  <li key={item.key}>
                    <Link
                      to={item.path}
                      onClick={closeSidebar}
                      className={mainLinkClass(item.path)}
                    >
                      {item.title}
                    </Link>
                  </li>
                );
              })}

            {!permissionLoading && isGuard && visibleMenu.length === 0 && (
              <li className="px-5 py-3 text-sm text-red-200">
                No menu permission assigned
              </li>
            )}
          </ul>

          <div className="border-t border-white/10">
            <button
              onClick={onLogout}
              className="w-full border-0 bg-transparent px-5 py-3.5 text-left text-sm text-red-200 hover:bg-red-500/10"
            >
              Logout
            </button>
          </div>
        </nav>
      </aside>
    </>
  );
}